import './styles.scss';

import { useMemo } from 'react';

import TableGrid from '../../../../../components/table-grid';
import {
  BacktesterScenario,
  BacktesterStrategyData,
} from '../../../../../providers/backtesting/api/backtester-config-api.types';
import { BacktestTableView } from '../shared/backtest-table-view';

interface SummaryProps {
  strategyData: BacktesterStrategyData;
  selectedStrategy: string;
}

const getStat = (scenario: BacktesterScenario, key: string): number | null => {
  const v = scenario.statistics?.[key];
  return typeof v === 'number' ? v : null;
};

const formatCurrency = (value: number | null): string => {
  if (value === null) return '-';
  const abs = Math.abs(value);
  const formatted = `$${abs.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  return value < 0 ? `-${formatted}` : formatted;
};

const formatWinRate = (scenario: BacktesterScenario): string => {
  const rate = getStat(scenario, 'win_rate');
  if (rate === null) return '-';
  const pct = `${(rate * 100).toFixed(1)}%`;
  const wins = scenario.statistics?.['win_count'];
  const total = scenario.statistics?.['trade_count'];
  if (typeof wins === 'number' && typeof total === 'number') {
    return `${pct} (${wins}/${total})`;
  }
  return pct;
};

const ColoredCell = ({
  value,
  formatter,
}: {
  value: number | null;
  formatter: (v: number | null) => string;
}) => {
  const cls = value === null ? '' : value > 0 ? 'positive' : value < 0 ? 'negative' : '';
  return <span className={cls}>{formatter(value)}</span>;
};

const COLUMNS = [
  { field: 'strategy', headerName: 'Backtest', align: 'left' as const, minWidth: 200 },
  {
    field: 'totalNetPnl',
    headerName: 'Total Net PNL',
    align: 'right' as const,
    minWidth: 140,
    cellRenderer: (params: { value: number | null }) => (
      <ColoredCell value={params.value} formatter={formatCurrency} />
    ),
  },
  {
    field: 'totalPremiumCollected',
    headerName: 'Total Premium Collected',
    align: 'right' as const,
    minWidth: 210,
    cellRenderer: (params: { value: number | null }) => (
      <ColoredCell value={params.value} formatter={formatCurrency} />
    ),
  },
  {
    field: 'totalPremiumLost',
    headerName: 'Total Premium Lost',
    align: 'right' as const,
    minWidth: 170,
    cellRenderer: (params: { value: number | null }) => (
      <ColoredCell value={params.value} formatter={formatCurrency} />
    ),
  },
  { field: 'winRate', headerName: 'Win Rate', align: 'right' as const, minWidth: 180 },
  { field: 'sharpe', headerName: 'Sharpe Ratio', align: 'right' as const, minWidth: 130 },
  {
    field: 'maxDrawdown',
    headerName: 'Max Drawdown',
    align: 'right' as const,
    minWidth: 140,
    cellRenderer: (params: { value: number | null }) => (
      <ColoredCell value={params.value} formatter={formatCurrency} />
    ),
  },
  {
    field: 'avgPremiumPerTrade',
    headerName: 'Avg Premium/Trade',
    align: 'right' as const,
    minWidth: 170,
    cellRenderer: (params: { value: number | null }) => (
      <ColoredCell value={params.value} formatter={formatCurrency} />
    ),
  },
  {
    field: 'avgPnlPerTrade',
    headerName: 'Avg PNL/Trade',
    align: 'right' as const,
    minWidth: 140,
    cellRenderer: (params: { value: number | null }) => (
      <ColoredCell value={params.value} formatter={formatCurrency} />
    ),
  },
];

// Scoped to the stratlet currently open in the result shell's overview table
// (mirrors the PP account card: each tab describes the one open account).
// Cross-stratlet comparison lives in that persistent overview table above.
export const Summary = ({ strategyData, selectedStrategy }: SummaryProps) => {
  const scenario = strategyData[selectedStrategy];

  const rowData = useMemo(() => {
    if (!scenario) return [{ strategy: selectedStrategy }];
    const sharpeRaw = scenario.statistics?.sharpe;
    return [
      {
        strategy: selectedStrategy,
        totalNetPnl: getStat(scenario, 'total_net_pnl'),
        totalPremiumCollected: getStat(scenario, 'total_premium_collected'),
        totalPremiumLost: getStat(scenario, 'total_premium_lost'),
        winRate: formatWinRate(scenario),
        sharpe: typeof sharpeRaw === 'number' ? sharpeRaw.toFixed(2) : '-',
        maxDrawdown: getStat(scenario, 'max_drawdown'),
        avgPremiumPerTrade: getStat(scenario, 'avg_premium_per_trade'),
        avgPnlPerTrade: getStat(scenario, 'avg_pnl_per_trade'),
      },
    ];
  }, [scenario, selectedStrategy]);

  return (
    <div className="backtest-result-summary">
      <BacktestTableView title="Performance Summary" filters={null}>
        <TableGrid columns={COLUMNS} rowData={rowData} autoHeight />
      </BacktestTableView>
    </div>
  );
};
